/**
 * "Add a question" dialog (manual MCQ / True-False) for the Quiz Editor.
 *
 * Extracted verbatim from QuizEditorPage.jsx during the Phase 3 refactor
 * (no behaviour change).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Plus, Trash2, Loader2 } from 'lucide-react';
import { useApi } from '../../../hooks/useApi';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { optUid } from './quizMeta';

export function AddQuestionDialog({ open, onClose, quizId, onCreated }) {
  const { post } = useApi();
  const [qType, setQType] = useState('mcq');
  const [text, setText] = useState('');
  const [options, setOptions] = useState([
    { _uid: optUid(), text: '', is_correct: true },
    { _uid: optUid(), text: '', is_correct: false },
  ]);
  const [explanation, setExplanation] = useState('');
  const [points, setPoints] = useState(1);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setQType('mcq');
    setText('');
    setOptions([
      { _uid: optUid(), text: '', is_correct: true },
      { _uid: optUid(), text: '', is_correct: false },
    ]);
    setExplanation('');
    setPoints(1);
  };

  useEffect(() => {
    if (qType === 'true_false') {
      setOptions([
        { _uid: optUid(), text: 'True', is_correct: true },
        { _uid: optUid(), text: 'False', is_correct: false },
      ]);
    } else if (options[0]?.text === 'True') {
      // Flipping back from true/false → restore an empty MCQ pair
      setOptions([
        { _uid: optUid(), text: '', is_correct: true },
        { _uid: optUid(), text: '', is_correct: false },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qType]);

  const setOpt = (i, patch) =>
    setOptions(prev => prev.map((o, idx2) => idx2 === i ? { ...o, ...patch } : o));
  const toggleCorrect = (i) =>
    setOptions(prev => prev.map((o, idx2) => ({ ...o, is_correct: idx2 === i })));
  const addOption = () =>
    setOptions(prev => [...prev, { _uid: optUid(), text: '', is_correct: false }]);
  const removeOption = (i) =>
    setOptions(prev => prev.filter((_, idx2) => idx2 !== i));

  // Validation: blank options are silently pruned on save; we just need
  // at least 2 non-empty options and one of them marked correct.
  const cleanOptions = options
    .map(o => ({ text: o.text.trim(), is_correct: !!o.is_correct }))
    .filter(o => o.text.length > 0);
  const canSave =
    text.trim().length > 0
    && cleanOptions.length >= 2
    && cleanOptions.some(o => o.is_correct);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await post(`/quizzes/${quizId}/questions`, {
        question_type: qType,
        question_text: text.trim(),
        points: Number(points) || 1,
        explanation: explanation.trim() || null,
        options: cleanOptions,
      });
      toast.success('Question added');
      resetForm();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-question-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Add a question</AlertDialogTitle>
          <AlertDialogDescription>
            Manual question added to this quiz. Defaults to multiple-choice; switch to True/False if you like.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Type:</Label>
            <select
              value={qType}
              onChange={(e) => setQType(e.target.value)}
              className="border rounded-md text-sm px-2 py-1 bg-background"
              data-testid="add-qtype"
            >
              <option value="mcq">Multiple choice</option>
              <option value="true_false">True / False</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-text">Question</Label>
            <Textarea
              id="add-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your question…"
              data-testid="add-text"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Options</Label>
            {options.map((o, i) => (
              <div key={o._uid} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleCorrect(i)}
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    o.is_correct ? 'border-emerald-500 bg-emerald-500' : 'border-muted-foreground/40'
                  }`}
                  data-testid={`add-option-correct-${i}`}
                >
                  {o.is_correct && <CheckCircle2 className="w-3 h-3 text-white" />}
                </button>
                <Input
                  value={o.text}
                  onChange={(e) => setOpt(i, { text: e.target.value })}
                  placeholder={`Option ${i + 1}`}
                  disabled={qType === 'true_false'}
                  data-testid={`add-option-text-${i}`}
                />
                {qType !== 'true_false' && options.length > 2 && (
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeOption(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
            {qType !== 'true_false' && options.length < 6 && (
              <Button variant="ghost" size="sm" onClick={addOption} data-testid="add-option">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add option
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="add-points">Points</Label>
              <Input id="add-points" type="number" min={0} step={0.5} value={points} onChange={(e) => setPoints(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-expl">Explanation (optional)</Label>
            <Textarea id="add-expl" value={explanation} onChange={(e) => setExplanation(e.target.value)} />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleCreate} disabled={!canSave || saving} data-testid="add-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Add question
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
